import { useMemo, useState } from 'react';
import { useCalendar } from '@/contexts/CalendarContext';
import { format, isSameDay, getHours, getMinutes } from 'date-fns';
import { ko } from 'date-fns/locale';
import { CalendarEvent } from '@/services/api';
import DiaryBookModal from '@/components/DiaryBookModal';
import TodayFortuneSection from '@/components/TodayFortuneSection';
import EditTodoModal from '@/components/EditTodoModal';
import { Todo } from '@/contexts/CalendarContext';

const HOURS = Array.from({ length: 10 }, (_, i) => i + 9); // 9시-18시

interface DayViewProps {
  events: CalendarEvent[]
  onCreateEvent: (date: Date) => void
  onEditEvent: (event: CalendarEvent) => void
  onDeleteEvent?: (eventId: string) => void
}

export default function DayView({ 
  events, 
  onCreateEvent, 
  onEditEvent,
  onDeleteEvent,
}: DayViewProps) {
  const { currentDate, getTodosForDate, deleteTodo, toggleTodo } = useCalendar();
  const [isDiaryOpen, setIsDiaryOpen] = useState(false);
  // const { diaryDates } = useDiaryData({ viewMode: 'day', currentDate });
  const [editingTodo, setEditingTodo] = useState<Todo | null>(null);
  
  // 현재 날짜에 일기가 있는지 확인

  // 현재 날짜의 할일 가져오기
  const dayTodos = useMemo(() => {
    return getTodosForDate(currentDate);
  }, [currentDate, getTodosForDate]);

  // 우선순위별 아이콘 가져오기
  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high': return '🔴';
      case 'medium': return '🟡';
      case 'low': return '🟢';
      default: return '🟡';
    }
  };

  // 시간별 할일과 일반 할일 분리
  const timedTodos = useMemo(() => dayTodos.filter(todo => todo.hasTime && todo.startTime), [dayTodos]);
  const generalTodos = useMemo(() => dayTodos.filter(todo => !todo.hasTime || !todo.startTime), [dayTodos]);


  const dayEvents = useMemo(() => {
    return events.filter(event => {
      const eventDate = new Date(event.start_time);
      return isSameDay(eventDate, currentDate);
    }).sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  }, [events, currentDate]);

  const allDayEvents = dayEvents.filter(event => event.all_day);
  const timedEvents = dayEvents.filter(event => !event.all_day);

  const currentHour = new Date().getHours();
  const currentMinute = new Date().getMinutes();
  const isWithinRange = currentHour >= 9 && currentHour <= 18;
  const currentTimePosition = isWithinRange ? ((currentHour - 9) * 60 + currentMinute) / (10 * 60) * 100 : -1;

  const getEventPosition = (event: CalendarEvent) => {
    const start = new Date(event.start_time);
    const end = new Date(event.end_time);
    const startHour = getHours(start);
    const endHour = getHours(end);
    const startMinutes = Math.max((startHour - 9) * 60 + getMinutes(start), 0);
    const endMinutes = Math.min((endHour - 9) * 60 + getMinutes(end), 10 * 60);
    
    const top = (startMinutes / (10 * 60)) * 100;
    const height = ((endMinutes - startMinutes) / (10 * 60)) * 100;
    
    return { top: `${top}%`, height: `${Math.max(height, 2)}%` };
  };

  return (
    <div className="h-full flex flex-col lg:flex-row bg-background">
      {/* Left side - Today's Fortune (50% on desktop, full width on mobile) */}
      <div className="lg:w-1/2 w-full lg:border-r border-b lg:border-b-0 border-border p-6 overflow-auto bg-gradient-to-br from-background to-muted/20 h-1/2 lg:h-full">
        {/* 날짜 정보 */}
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-foreground mb-1">
            {format(currentDate, 'yyyy년 M월 d일', { locale: ko })}
          </h2>
          <p className="text-xl text-muted-foreground">
            {format(currentDate, 'EEEE', { locale: ko })}
          </p>
        </div>

        {/* 빠른 작성 버튼 그룹 */}
        <div className="mb-4 p-4 rounded-lg bg-gradient-to-r from-primary-500/10 to-primary-600/10 border border-primary-500/20">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">빠른 작성</h3>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => {
                const eventDate = new Date(currentDate);
                eventDate.setHours(new Date().getHours(), 0, 0, 0);
                onCreateEvent(eventDate);
              }}
              className="px-3 py-2 text-sm bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 rounded-md transition-colors flex items-center justify-center gap-1"
            >
              <span>📅</span> 일정
            </button>
            <button
              onClick={() => {
                const eventDate = new Date(currentDate);
                eventDate.setHours(new Date().getHours(), 0, 0, 0);
                onCreateEvent(eventDate);
              }}
              className="px-3 py-2 text-sm bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 rounded-md transition-colors flex items-center justify-center gap-1"
            >
              <span>✅</span> 할일
            </button>
            <button
              onClick={() => setIsDiaryOpen(true)}
              className="px-3 py-2 text-sm bg-purple-500/10 hover:bg-purple-500/20 text-purple-600 rounded-md transition-colors flex items-center justify-center gap-1"
            >
              <span>📖</span> 일기
            </button>
          </div>
        </div>

        {/* 오늘의 운세 섹션 - 확장된 UI */}
        <div className="h-full flex flex-col">
          <TodayFortuneSection 
            currentDate={currentDate} 
          />
          
        </div>
      </div>

      {/* Right side - Calendar Timeline (50% on desktop, full width on mobile) */}
      <div className="lg:w-1/2 w-full overflow-auto h-1/2 lg:h-full">
        {/* 종일 일정 상단 표시 */}
        {allDayEvents.length > 0 && (
          <div className="p-4 border-b border-border bg-muted/30">
            <h3 className="text-sm font-semibold text-muted-foreground mb-2">종일 일정</h3>
            <div className="flex flex-wrap gap-2">
              {allDayEvents.map(event => (
                <div
                  key={event.id}
                  className="group inline-flex items-center px-3 py-1 rounded-full text-sm cursor-pointer hover:opacity-80 transition-opacity"
                  style={{ 
                    backgroundColor: event.color || '#3b82f6',
                    color: 'white',
                  }}
                  onClick={() => onEditEvent(event)}
                  title={event.description || event.title}
                >
                  <span>{event.title}</span>
                  {onDeleteEvent && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(`"${event.title}" 일정을 삭제하시겠습니까?`)) {
                          onDeleteEvent(event.id!);
                        }
                      }}
                      className="opacity-0 group-hover:opacity-100 ml-2 hover:text-red-200 transition-opacity"
                      title="일정 삭제"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="relative min-h-full">
          {/* Current time indicator */}
          {isSameDay(currentDate, new Date()) && currentTimePosition >= 0 && (
            <div 
              className="absolute left-0 right-0 z-20 pointer-events-none"
              style={{ top: `${currentTimePosition}%` }}
            >
              <div className="flex items-center">
                <div className="w-20"></div>
                <div className="flex-1 h-0.5 bg-red-500"></div>
                <div className="w-3 h-3 bg-red-500 rounded-full -ml-1.5"></div>
              </div>
            </div>
          )}

          {/* Hours grid */}
          <div className="relative">
            {HOURS.map(hour => (
              <div key={hour} className="flex border-b border-border/30" style={{ height: '100px' }}>
                <div className="w-20 px-4 py-3 text-muted-foreground text-right">
                  <div className="text-xl font-semibold">
                    {format(new Date().setHours(hour, 0, 0, 0), 'HH:mm')}
                  </div>
                </div>
                <div 
                  className="flex-1 relative cursor-pointer hover:bg-muted/20 transition-colors group"
                  onClick={() => {
                    const eventDate = new Date(currentDate);
                    eventDate.setHours(hour, 0, 0, 0);
                    onCreateEvent(eventDate);
                  }}
                  title={`${hour}:00에 일정 추가`}
                >
                  {/* Half-hour line */}
                  <div className="absolute top-1/2 left-0 right-0 border-t border-border/15"></div>
                  {/* 호버 시 표시되는 + 아이콘 */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center">
                      <span className="text-primary-600 text-lg">+</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Timed events and todos overlay */}
            <div className="absolute top-0 left-20 right-0 bottom-0">
              {/* Events */}
              {timedEvents.map(event => {
                const position = getEventPosition(event);
                return (
                  <div
                    key={event.id}
                    className="absolute left-4 right-1/2 p-4 rounded-xl cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] mr-2 group"
                    style={{ 
                      ...position,
                      backgroundColor: `${event.color || '#3b82f6'}15`,
                      borderLeft: `4px solid ${event.color || '#3b82f6'}`,
                      border: `1px solid ${event.color || '#3b82f6'}30`,
                    }}
                    onClick={() => onEditEvent(event)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="text-sm font-semibold mb-1" style={{ color: event.color || '#3b82f6' }}>
                          {format(new Date(event.start_time), 'HH:mm')} - {format(new Date(event.end_time), 'HH:mm')}
                        </div>
                        <div className="font-semibold text-foreground text-lg mb-1">{event.title}</div>
                        {event.description && (
                          <div className="text-sm text-muted-foreground">{event.description}</div>
                        )}
                        {event.location && (
                          <div className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                            <span>📍</span> {event.location}
                          </div>
                        )}
                      </div>
                      {onDeleteEvent && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm(`"${event.title}" 일정을 삭제하시겠습니까?`)) {
                              onDeleteEvent(event.id!);
                            }
                          }}
                          className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity ml-2 text-lg"
                          title="일정 삭제"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {/* Timed Todos */}
              {timedTodos.map(todo => {
                const [hours, minutes] = (todo.startTime || '09:00').split(':').map(Number);
                const todoDate = new Date(currentDate);
                todoDate.setHours(hours, minutes, 0, 0);
                
                const topPosition = Math.max(((hours - 9) * 60 + minutes) / (10 * 60) * 100, 0);
                
                return (
                  <div
                    key={todo.id}
                    className="absolute left-1/2 right-4 p-3 rounded-lg cursor-pointer hover:shadow-md transition-all ml-2"
                    style={{ 
                      top: `${topPosition}%`,
                      height: '40px',
                      backgroundColor: todo.completed ? '#10b98115' : '#fbbf2415',
                      borderLeft: `3px solid ${todo.completed ? '#10b981' : '#fbbf24'}`,
                      opacity: todo.completed ? 0.7 : 1,
                    }}
                    onClick={() => toggleTodo(todo.id)}
                  >
                    <div className="flex items-center justify-between h-full">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={todo.completed}
                          onChange={() => toggleTodo(todo.id)}
                          className="w-4 h-4"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="text-sm font-medium">{getPriorityIcon(todo.priority)}</span>
                        <span className={`text-sm ${todo.completed ? 'line-through text-muted-foreground' : ''}`}>
                          {todo.text}
                        </span>
                        <span className="text-xs text-muted-foreground">({todo.startTime})</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteTodo(todo.id);
                        }}
                        className="text-red-500 hover:text-red-700 text-sm px-2"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* General Todos Section (at bottom) */}
        {generalTodos.length > 0 && (
          <div className="p-4 border-t border-border bg-muted/10">
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">시간 미지정 할일</h3>
            <div className="space-y-2">
              {generalTodos.map(todo => (
                <div
                  key={todo.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-background border border-border hover:shadow-sm transition-all"
                  style={{
                    opacity: todo.completed ? 0.6 : 1,
                  }}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <input
                      type="checkbox"
                      checked={todo.completed}
                      onChange={() => toggleTodo(todo.id)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">{getPriorityIcon(todo.priority)}</span>
                    <span 
                      className={`text-sm flex-1 cursor-pointer hover:underline ${todo.completed ? 'line-through text-muted-foreground' : ''}`}
                      onClick={() => setEditingTodo(todo)}
                    >
                      {todo.text}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setEditingTodo(todo)}
                      className="text-blue-500 hover:text-blue-700 text-sm px-2 py-1"
                    >
                      수정
                    </button>
                    <button
                      onClick={() => deleteTodo(todo.id)}
                      className="text-red-500 hover:text-red-700 text-sm px-2 py-1"
                    >
                      삭제
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Diary Book Modal */}
      <DiaryBookModal 
        isOpen={isDiaryOpen}
        onClose={() => setIsDiaryOpen(false)}
        date={currentDate}
        onSave={() => {
          setIsDiaryOpen(false);
        }}
      />
      
      {/* 할일 수정 모달 */}
      <EditTodoModal
        isOpen={!!editingTodo}
        onClose={() => setEditingTodo(null)}
        todo={editingTodo}
      />
    </div>
  );
}